import {
  FloatType,
  FramebufferTexture,
  HalfFloatType,
  NoBlending,
  RawShaderMaterial,
  RGBFormat,
  WebGLRenderTarget,
  WebGLRenderer
} from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { halton } from './render-studio/render-studio.options';

const VERTEX = /* glsl */ `
  precision highp float;
  uniform mat4 modelViewMatrix;
  uniform mat4 projectionMatrix;
  attribute vec3 position;
  attribute vec2 uv;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;

/** Running average of canvas frames: output = mix(previous, frame, 1 / n). */
const ACCUMULATE_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform sampler2D tPrevious;
  uniform sampler2D tFrame;
  uniform float weight;
  varying vec2 vUv;
  void main() {
    gl_FragColor = mix(texture2D(tPrevious, vUv), vec4(texture2D(tFrame, vUv).rgb, 1.0), weight);
  }`;

/** The average back onto the 8-bit canvas, with a half-LSB dither against banding. */
const PRESENT_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform sampler2D tAccum;
  varying vec2 vUv;
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
  }
  void main() {
    vec3 color = texture2D(tAccum, vUv).rgb + (hash(gl_FragCoord.xy) - 0.5) / 255.0;
    gl_FragColor = vec4(color, 1.0);
  }`;

/**
 * Supersamples whatever `drawFrame` puts on the canvas. `drawFrame` is called
 * `samples` times with a sub-pixel offset (in pixels, within ±0.5) and must
 * draw the full frame to the canvas, shifted by it. Each frame is copied off
 * the canvas and averaged in float, so the result is anti-aliased exactly as
 * the canvas shows it: tone mapping and colour space stay per material, as
 * on screen. The average is left on the canvas, `width` × `height` pixels,
 * ready to read back before the browser composites.
 */
export function renderSupersampled(
  renderer: WebGLRenderer,
  width: number,
  height: number,
  samples: number,
  drawFrame: (offsetX: number, offsetY: number) => void
): void {
  const accumType = renderer.extensions.has('EXT_color_buffer_float') ? FloatType : HalfFloatType;
  let accumRead = new WebGLRenderTarget(width, height, { type: accumType, depthBuffer: false });
  let accumWrite = new WebGLRenderTarget(width, height, { type: accumType, depthBuffer: false });
  // RGB: the canvas has no alpha channel, and a copy can't add components the framebuffer lacks.
  const frame = new FramebufferTexture(width, height);
  frame.format = RGBFormat;
  frame.internalFormat = 'RGB8';
  const accumulate = new RawShaderMaterial({
    uniforms: { tPrevious: { value: null }, tFrame: { value: frame }, weight: { value: 1 } },
    vertexShader: VERTEX,
    fragmentShader: ACCUMULATE_FRAGMENT,
    blending: NoBlending,
    depthTest: false,
    depthWrite: false
  });
  const present = new RawShaderMaterial({
    uniforms: { tAccum: { value: null } },
    vertexShader: VERTEX,
    fragmentShader: PRESENT_FRAGMENT,
    blending: NoBlending,
    depthTest: false,
    depthWrite: false
  });
  const quad = new FullScreenQuad(accumulate);
  const previousTarget = renderer.getRenderTarget();

  try {
    const total = Math.max(1, Math.round(samples));
    for (let i = 0; i < total; i++) {
      renderer.setRenderTarget(null);
      drawFrame(halton(i + 1, 2) - 0.5, halton(i + 1, 3) - 0.5);
      renderer.setRenderTarget(null);
      renderer.copyFramebufferToTexture(frame);

      accumulate.uniforms['tPrevious'].value = accumRead.texture;
      accumulate.uniforms['weight'].value = 1 / (i + 1);
      quad.material = accumulate;
      renderer.setRenderTarget(accumWrite);
      quad.render(renderer);
      [accumRead, accumWrite] = [accumWrite, accumRead];
    }

    present.uniforms['tAccum'].value = accumRead.texture;
    quad.material = present;
    renderer.setRenderTarget(null);
    quad.render(renderer);
  } finally {
    renderer.setRenderTarget(previousTarget);
    accumRead.dispose();
    accumWrite.dispose();
    frame.dispose();
    accumulate.dispose();
    present.dispose();
    quad.dispose();
  }
}
